/**
 * Document authorization checks for UX (UI visibility/hiding)
 *
 * IMPORTANT: Backend fully enforces authorization - these are UX checks only!
 *
 * Purpose: Hide unauthorized UI to improve user experience
 * - Shows/hides buttons, forms, sections based on role + obvious state
 * - Backend is the source of truth and will reject unauthorized actions
 * - Keep checks simple: only use role + data we already have in the frontend
 *
 * Philosophy: Aim to hide ~95% of unauthorized UI with simple checks.
 * The remaining ~5% that slips through is handled gracefully with backend
 * error responses (e.g., "Permission denied" toast messages).
 */

import type {AuthUser} from '@/matic/types/auth';
import type {Document} from '../types/document';

/**
 * Can user create documents?
 *
 * Simple role check - basic users cannot create.
 * Backend enforces full business rules (tenant limits, quotas, etc.)
 */
export function canCreateDocument(user: AuthUser): boolean {
  return user.role !== 'basic';
}

/**
 * Can user edit this document?
 *
 * Checks:
 * - Role (admin/quality can edit any, normal can edit own)
 * - Ownership (for normal users)
 *
 * Backend also checks: document status, approval state, locks, etc.
 */
export function canEditDocument(user: AuthUser, document: Document): boolean {
  // Admin and quality can edit any document
  if (user.role === 'admin' || user.role === 'quality') {
    return true;
  }

  // Normal users can edit their own documents
  if (user.role === 'normal') {
    return document.createdBy === user.id;
  }

  // Basic users cannot edit
  return false;
}

/**
 * Can user delete this document?
 *
 * Only admin/quality can delete (same as edit for now).
 * Backend enforces additional rules (published status, dependencies, etc.)
 */
export function canDeleteDocument(user: AuthUser, document: Document): boolean {
  // Only admin and quality can delete
  return user.role === 'admin' || user.role === 'quality';
}

/**
 * Can user view this document?
 *
 * Most users can view most documents.
 * Backend enforces tenant isolation, publish status for basic users, etc.
 */
export function canViewDocument(user: AuthUser, document: Document): boolean {
  // Admin and quality can view all documents
  if (user.role === 'admin' || user.role === 'quality') {
    return true;
  }

  // Normal users can view all documents (backend filters by tenant/ownership)
  if (user.role === 'normal') {
    return true;
  }

  // Basic users can only view published documents
  if (user.role === 'basic') {
    return document.status === 'published';
  }

  return false;
}
